import type { EstimateLevel, Preventivo } from '../types';

/**
 * Modalità di calcolo dell'importo per le voci CME.
 * - 'stimato': importo stimato (default, fallback su costoMed)
 * - 'reale':   importo realmente speso (fallback su 'stimato' se non compilato)
 * - 'usato' / 'nuovo': per le voci a doppia stima, usa il prezzo della variante
 */
export type CmeMode = 'stimato' | 'reale' | 'usato' | 'nuovo';

/**
 * Get the price from a Preventivo based on the estimate level
 */
export function getPrice(item: Preventivo, level: EstimateLevel): number {
  const key = `costo${level.charAt(0).toUpperCase() + level.slice(1)}` as keyof Pick<Preventivo, 'costoMin' | 'costoMed' | 'costoMax'>;
  return parseFloat(item[key]) || 0;
}

/**
 * Format a number as Euro currency
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a number as percentage
 */
export function formatPercent(value: number, decimals = 2): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Parse a string decimal to number
 */
export function parseDecimal(value: string | null | undefined): number {
  if (!value) return 0;
  return parseFloat(value) || 0;
}

/**
 * Get label for estimate level
 */
export function getEstimateLevelLabel(level: EstimateLevel): string {
  const labels: Record<EstimateLevel, string> = {
    min: 'Minimo',
    med: 'Medio',
    max: 'Massimo',
  };
  return labels[level];
}

/**
 * Calculate min, med, max totals for an array of preventivi
 */
export function calculatePreventiviTotals(preventivi: Preventivo[]): {
  min: number;
  med: number;
  max: number;
} {
  return preventivi.reduce(
    (acc, p) => ({
      min: acc.min + parseDecimal(p.costoMin),
      med: acc.med + parseDecimal(p.costoMed),
      max: acc.max + parseDecimal(p.costoMax),
    }),
    { min: 0, med: 0, max: 0 }
  );
}

// ============================================================================
// CME — importi stimato/reale + toggle usato/nuovo
// ============================================================================

/**
 * True se la voce ha una doppia stima usato/nuovo selezionabile via toggle.
 */
export function hasUsatoNuovo(p: Preventivo): boolean {
  return p.tipoAcquisto === 'usato' || p.tipoAcquisto === 'nuovo';
}

/**
 * True se la voce è attiva (conta nei totali). Default true se il campo è
 * undefined (compatibilità con dati/voci precedenti al campo `attivo`).
 */
export function isAttiva(p: Preventivo): boolean {
  return p.attivo !== false;
}

/**
 * Importo "stimato" di una voce CME.
 * Per le voci a doppia stima rispetta la variante scelta (tipoAcquisto):
 * prezzoUsato/prezzoNuovo se presenti, altrimenti importoStimato.
 * Fallback finale su costoMed per le voci non-CME (compatibilità).
 */
export function getImportoStimato(p: Preventivo): number {
  if (p.tipoAcquisto === 'usato' && p.prezzoUsato != null) return parseDecimal(p.prezzoUsato);
  if (p.tipoAcquisto === 'nuovo' && p.prezzoNuovo != null) return parseDecimal(p.prezzoNuovo);
  if (p.importoStimato != null && p.importoStimato !== '') return parseDecimal(p.importoStimato);
  return parseDecimal(p.costoMed);
}

/**
 * Importo di una voce CME secondo la modalità richiesta.
 * - 'reale': importoReale se compilato, altrimenti l'importo stimato
 * - 'usato'/'nuovo': forza la variante a prescindere dal tipoAcquisto salvato
 * - 'stimato' (default): rispetta la variante salvata
 */
export function getImporto(p: Preventivo, mode: CmeMode = 'stimato'): number {
  switch (mode) {
    case 'reale':
      return p.importoReale != null && p.importoReale !== ''
        ? parseDecimal(p.importoReale)
        : getImportoStimato(p);
    case 'usato':
      if (p.prezzoUsato != null) return parseDecimal(p.prezzoUsato);
      return getImportoStimato(p);
    case 'nuovo':
      if (p.prezzoNuovo != null) return parseDecimal(p.prezzoNuovo);
      return getImportoStimato(p);
    case 'stimato':
    default:
      return getImportoStimato(p);
  }
}

/**
 * Δ = Reale − Stimato per una singola voce (0 se reale non compilato).
 */
export function getDelta(p: Preventivo): number {
  if (p.importoReale == null || p.importoReale === '') return 0;
  return parseDecimal(p.importoReale) - getImportoStimato(p);
}

/**
 * Totali CME su un array di preventivi: stimato, reale (con fallback su stimato
 * dove non compilato) e delta complessivo.
 * Le voci disattivate (`attivo === false`) sono ESCLUSE dai totali (contributo 0).
 */
export function calculateCmeTotals(preventivi: Preventivo[]): {
  stimato: number;
  reale: number;
  delta: number;
} {
  return preventivi.reduce(
    (acc, p) => {
      if (!isAttiva(p)) return acc;
      const stimato = getImportoStimato(p);
      const reale = getImporto(p, 'reale');
      return {
        stimato: acc.stimato + stimato,
        reale: acc.reale + reale,
        delta: acc.delta + getDelta(p),
      };
    },
    { stimato: 0, reale: 0, delta: 0 }
  );
}

/**
 * Riepilogo delle voci disattivate: conteggio e importo stimato complessivo
 * escluso dai totali.
 */
export function calculateEscluse(preventivi: Preventivo[]): {
  count: number;
  stimato: number;
} {
  return preventivi.reduce(
    (acc, p) => {
      if (isAttiva(p)) return acc;
      return { count: acc.count + 1, stimato: acc.stimato + getImportoStimato(p) };
    },
    { count: 0, stimato: 0 }
  );
}
